<template>
  <div class="graph-page">
    <!-- ── Controls bar ── -->
    <div class="ctrl-bar">
      <div class="ctrl-row">
        <span class="brand">TRX Flow Graph</span>

        <input
          v-model="address"
          class="addr-input"
          placeholder="Tron address  T…"
          @keyup.enter="run"
        />

        <label class="param-lbl">Back <select v-model.number="n" class="param-sel">
          <option v-for="i in 4" :key="i" :value="i">{{ i }}</option>
        </select></label>

        <label class="param-lbl">Fwd <select v-model.number="m" class="param-sel">
          <option v-for="i in 4" :key="i" :value="i">{{ i }}</option>
        </select></label>

        <label class="param-lbl">Max/lvl <select v-model.number="maxPerLevel" class="param-sel">
          <option v-for="v in [5,8,12,20]" :key="v" :value="v">{{ v }}</option>
        </select></label>

        <button class="btn-run" @click="run" :disabled="loading">
          <span v-if="loading" class="spin"></span>
          {{ loading ? progressMsg || 'Loading…' : 'Build Graph' }}
        </button>

        <router-link to="/tron/transactions" class="btn-back">← Back</router-link>
      </div>

      <!-- Progress bar -->
      <div v-if="loading" class="prog-wrap">
        <div class="prog-bar" :style="{ width: pct + '%' }"></div>
      </div>
    </div>

    <!-- ── Error ── -->
    <div v-if="error" class="err-banner">{{ error }}</div>

    <!-- ── Stats bar (when graph is loaded) ── -->
    <div v-if="graph && !loading" class="stats-bar">
      <span>{{ graph.nodes.length }} nodes</span>
      <span class="sep">·</span>
      <span>{{ graph.edges.length }} edges</span>
      <span class="sep">·</span>
      <span class="origin-addr" :title="address">{{ address }}</span>
      <span class="sep">·</span>
      <span class="param-summary">Back {{ n }} · Fwd {{ m }} · Max {{ maxPerLevel }}/level</span>
    </div>

    <!-- ── Graph canvas ── -->
    <TxGraphCanvas
      v-if="graph"
      :graph="graph"
      :origin="address"
      class="canvas-area"
      @navigate="onNavigate"
    />

    <!-- ── Empty state ── -->
    <div v-else-if="!loading" class="empty">
      <div class="empty-hex">⬡</div>
      <p class="empty-title">TRX Transaction Graph</p>
      <p class="empty-sub">Enter a Tron address, choose depth, and build the graph.</p>
      <p class="empty-sub">
        N levels <strong>before</strong> = who funded this address.<br>
        M levels <strong>after</strong> = where the funds went.
      </p>
    </div>
  </div>
</template>

<script>
import TxGraphCanvas from '../components/TxGraphCanvas.vue';
import { buildTxGraph } from '../services/graphService';

export default {
  name: 'TronGraph',
  components: { TxGraphCanvas },

  data() {
    return {
      address: '',
      n: 2,
      m: 2,
      maxPerLevel: 8,
      loading: false,
      error: null,
      graph: null,
      pct: 0,
      progressMsg: '',
    };
  },

  methods: {
    async run() {
      const addr = this.address.trim();
      if (!addr) return;
      this.loading = true;
      this.error = null;
      this.graph = null;
      this.pct = 4;
      this.progressMsg = 'Starting…';

      const total = this.n + this.m + 1;
      let done = 0;

      try {
        const graph = await buildTxGraph(
          addr,
          { n: this.n, m: this.m, maxPerLevel: this.maxPerLevel },
          (ev) => {
            if (ev.phase === 'before') {
              done = ev.level;
              this.progressMsg = `Back level ${ev.level}/${this.n}  (${ev.nodes} nodes)`;
            } else if (ev.phase === 'after') {
              done = this.n + ev.level;
              this.progressMsg = `Fwd level ${ev.level}/${this.m}  (${ev.nodes} nodes)`;
            } else if (ev.phase === 'balances') {
              done = this.n + this.m;
              this.progressMsg = `Fetching balances (${ev.nodes} addresses)…`;
            } else {
              done = total;
              this.progressMsg = `${ev.nodes} nodes · ${ev.edges} edges`;
            }
            this.pct = Math.round(4 + (done / total) * 92);
          }
        );
        this.graph = graph;
        this.pct = 100;
      } catch (e) {
        this.error = 'Graph build failed: ' + (e.message || e);
      } finally {
        this.loading = false;
      }
    },

    onNavigate(addr) {
      this.address = addr;
      this.run();
    },
  },

  mounted() {
    const q = this.$route?.query || {};
    if (q.address) {
      this.address = q.address;
      if (q.n) this.n = parseInt(q.n) || 2;
      if (q.m) this.m = parseInt(q.m) || 2;
      if (q.max) this.maxPerLevel = parseInt(q.max) || 8;
      this.$nextTick(this.run);
    } else {
      const w = this.$store?.state?.wallets?.tron;
      if (w?.address) this.address = w.address;
    }
  },
};
</script>

<style scoped>
.graph-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #04040a;
  color: #ccc;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* Controls bar */
.ctrl-bar {
  flex-shrink: 0;
  background: #0c0c1c;
  border-bottom: 1px solid rgba(255,0,20,0.25);
  padding: .55rem 1rem .4rem;
}
.ctrl-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: .45rem;
}
.brand {
  font-weight: 700;
  font-size: 1rem;
  color: #ff0013;
  letter-spacing: .03em;
  white-space: nowrap;
  margin-right: .25rem;
}
.addr-input {
  width: 300px;
  background: #14142a;
  border: 1px solid #2a2a44;
  border-radius: 6px;
  padding: .3rem .6rem;
  font-size: .8rem;
  color: #ddd;
  outline: none;
  transition: border-color .2s;
}
.addr-input:focus { border-color: #ff0013; }
.param-lbl {
  display: flex;
  align-items: center;
  gap: .3rem;
  font-size: .75rem;
  color: #777;
  white-space: nowrap;
}
.param-sel {
  background: #14142a;
  border: 1px solid #2a2a44;
  border-radius: 4px;
  color: #ccc;
  font-size: .75rem;
  padding: .2rem .3rem;
  width: 52px;
}
.btn-run {
  display: flex;
  align-items: center;
  gap: .4rem;
  background: #cc0010;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: .8rem;
  font-weight: 600;
  padding: .32rem .85rem;
  cursor: pointer;
  transition: background .15s;
  white-space: nowrap;
}
.btn-run:hover:not(:disabled) { background: #ff0013; }
.btn-run:disabled { opacity: .6; cursor: not-allowed; }
.spin {
  display: inline-block;
  width: .7rem;
  height: .7rem;
  border: 2px solid rgba(255,255,255,.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.btn-back {
  margin-left: .2rem;
  font-size: .75rem;
  color: #666;
  text-decoration: none;
  border: 1px solid #2a2a44;
  border-radius: 5px;
  padding: .28rem .6rem;
  transition: color .15s, border-color .15s;
  white-space: nowrap;
}
.btn-back:hover { color: #ccc; border-color: #555; }

/* Progress bar */
.prog-wrap {
  height: 3px;
  background: #1a1a2e;
  margin-top: .4rem;
  border-radius: 2px;
  overflow: hidden;
}
.prog-bar {
  height: 100%;
  background: linear-gradient(90deg, #cc0010, #ff6600);
  transition: width .35s;
  border-radius: 2px;
  animation: shimmer 1.8s linear infinite;
  background-size: 200% 100%;
}
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Error */
.err-banner {
  background: #2a0010;
  border-bottom: 1px solid #660022;
  color: #ff8888;
  font-size: .8rem;
  padding: .4rem 1rem;
  flex-shrink: 0;
}

/* Stats bar */
.stats-bar {
  display: flex;
  align-items: center;
  gap: .5rem;
  padding: .25rem 1rem;
  background: rgba(255,255,255,0.02);
  border-bottom: 1px solid rgba(255,255,255,0.05);
  font-size: .72rem;
  color: #555;
  flex-shrink: 0;
}
.sep { color: #333; }
.origin-addr {
  font-family: monospace;
  color: #888;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.param-summary { color: #444; }

/* Canvas */
.canvas-area { flex: 1; overflow: hidden; }

/* Empty state */
.empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .6rem;
  color: #3a3a55;
  padding: 2rem;
  text-align: center;
}
.empty-hex { font-size: 5rem; opacity: .18; }
.empty-title { font-size: 1.1rem; color: #555; font-weight: 600; margin: 0; }
.empty-sub { font-size: .82rem; margin: 0; line-height: 1.6; }
.empty-sub strong { color: #777; }
</style>
